import React, { cloneElement, useRef, useState } from 'react'
import {
  useFloating,
  useInteractions,
  Placement,
  offset,
  flip,
  shift,
  autoUpdate,
  useDismiss,
  FloatingPortal,
  useFocus,
  useRole,
  arrow,
  useHover,
  useClick,
} from '@floating-ui/react-dom-interactions'

import styled, { useTheme } from 'styled-components'
import { POPOVER_ID } from 'components/styled/popover/popover-root'
import { ThemeSpace } from 'theme/spaces'

export interface PopoverTooltipProps {
  rootId?: string
  placement?: Placement
  defaultIsOpen?: boolean
  trigger: React.ReactElement<{ [key: string]: any }>
  width?: ThemeSpace
  maxHeight?: ThemeSpace
  interaction?: 'onClick' | 'onHover'
  children?: React.ReactNode
  modalPopover?: boolean
}

const StyledArrow = styled.div`
  position: absolute;
  width: ${({ theme }) => theme.space.s8};
  height: ${({ theme }) => theme.space.s8};
  background-color: ${({ theme }) => theme.color.white};
  visibility: hidden;

  &::before {
    position: absolute;
    width: ${({ theme }) => theme.space.s8};
    height: ${({ theme }) => theme.space.s8};
    background: inherit;

    visibility: visible;
    content: '';
    transform: rotate(45deg);
  }
`

// There are two tooltip arrows rendered on top of each other (white and grey). The grey arrow is 2px larger, so it looks like the white arrow has grey border.
const StyledGreyArrow = styled(StyledArrow)`
  background-color: ${({ theme }) => theme.color.grey10};

  width: calc(${({ theme }) => theme.space.s8} + 2px);
  height: calc(${({ theme }) => theme.space.s8} + 2px);

  &::before {
    width: calc(${({ theme }) => theme.space.s8} + 2px);
    height: calc(${({ theme }) => theme.space.s8} + 2px);
  }
`

const StyledTooltipWrapper = styled.div<{
  $modalPopover: boolean
}>`
  z-index: ${({ theme, $modalPopover }) =>
    $modalPopover ? theme.zIndex.modalPopover : theme.zIndex.popover};
  display: flex;
  box-shadow: ${({ theme }) => theme.shadow.default};
  border-radius: ${({ theme }) => theme.space.s4};
`

const StyledTooltip = styled.div<{
  $width: ThemeSpace
  $maxHeight: ThemeSpace
}>`
  position: relative;
  display: flex;
  cursor: pointer;

  background-color: ${({ theme }) => theme.color.white};
  border: 1px solid ${({ theme }) => theme.color.grey10};
  border-radius: ${({ theme }) => theme.space.s4};

  width: ${({ theme, $width }) => theme.space[$width]};
  max-width: 90vw;
  max-height: ${({ theme, $maxHeight }) => theme.space[$maxHeight]};
`

const TooltipContent = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  padding: ${({ theme }) => theme.space.s8};
  overflow: auto;
`

// This tooltip is based on floating-ui example: https://codesandbox.io/s/winter-tree-wmmffl?file=/src/Tooltip.tsx

export const PopoverTooltip: React.FC<PopoverTooltipProps> = ({
  placement = 'bottom',
  rootId = POPOVER_ID,
  defaultIsOpen = false,
  trigger,
  children,
  width = 's160',
  maxHeight = 's240',
  interaction = 'onHover',
  modalPopover = false,
}) => {
  const theme = useTheme()
  const arrowRef = useRef(null)
  const [isTooltipOpen, setTooltipOpen] = useState(defaultIsOpen)
  const {
    x,
    y,
    reference,
    floating,
    strategy,
    context,
    placement: finalPlacement,
    middlewareData: { arrow: { x: arrowX, y: arrowY } = {} },
  } = useFloating({
    open: isTooltipOpen,
    onOpenChange: setTooltipOpen,
    placement,
    middleware: [
      offset(12),
      flip(), // flips the tooltip to the top/bottom of the button if default location (placement) has not enough space
      shift(), // shifts the tooltip to the left/right of the button if there's not enough space
      arrow({ element: arrowRef }),
    ],
    whileElementsMounted: autoUpdate, // updates the tooltip location when it is open
  })
  const { getReferenceProps, getFloatingProps } = useInteractions([
    ...(interaction === 'onHover'
      ? [useHover(context), useFocus(context)]
      : [useClick(context)]),
    useRole(context, { role: 'tooltip' }),
    useDismiss(context),
  ])

  // Determines arrow static side. For example: if the tooltip is placed on bottom of the trigger element => arrow needs to be placed on top.
  const staticSide =
    {
      top: 'bottom',
      right: 'left',
      bottom: 'top',
      left: 'right',
    }[finalPlacement.split('-')[0]] || ''

  return (
    <>
      {/* Trigger element is cloned, so that ref and other props (event listeners) that the floating-ui tooltip requires can be passed to it */}
      {cloneElement(
        trigger,
        getReferenceProps({ ref: reference, ...trigger.props })
      )}
      {isTooltipOpen && (
        <FloatingPortal id={rootId}>
          <StyledTooltipWrapper
            ref={floating}
            style={{
              position: strategy,
              top: y ?? 0,
              left: x ?? 0,
            }}
            {...getFloatingProps()}
            $modalPopover={modalPopover}
          >
            <StyledGreyArrow
              style={{
                top: arrowY ?? '',
                left: arrowX ?? '',
                right: '',
                bottom: '',
                [staticSide]: `-${theme.space.s4}`,
              }}
            />
            <StyledTooltip $width={width} $maxHeight={maxHeight}>
              <TooltipContent>{children}</TooltipContent>
              <StyledArrow
                style={{
                  top: arrowY ?? '',
                  left: arrowX ?? '',
                  right: '',
                  bottom: '',
                  [staticSide]: `-${theme.space.s4}`,
                }}
                ref={arrowRef}
              />
            </StyledTooltip>
          </StyledTooltipWrapper>
        </FloatingPortal>
      )}
    </>
  )
}
